import type { AppState, Member, Product } from './types';
import { createCommunitySeed } from '../community/demo';
import { demoMemberMedia } from './demoMedia';
export const DEMO_KEY = 'ggb.local-demo.v3';
export const DEMO_PREVIEW_KEY = 'ggb.local-demo.preview';

/** Browser-only previews cannot override a configured account connection. */
export function demoPreviewRequested(
  search: string,
  allowed: boolean,
  saved = false,
  liveConfigured = false,
) {
  if (!allowed || liveConfigured) return false;
  const value = new URLSearchParams(search).get('demo');
  return value === '1' || (value !== '0' && saved);
}
export const COMPANY_DEMO_KEY = 'ggb.local-demo.companies.v1';

const imageFields = new Set([
  'image',
  'image_url',
  'imageUrl',
  'avatar_url',
  'avatarUrl',
  'gym_cover_url',
  'cover_url',
  'coverUrl',
  'thumbnail_url',
  'thumbnailUrl',
  'poster_url',
  'posterUrl',
  'dataUrl',
]);
const imageMaps = new Set(['post_images', 'story_images', 'images']);

/** Keep shared image references while excluding browser-owned photo payloads. */
function withoutBrowserImages(value: unknown, field = '', parent = ''): unknown {
  if (typeof value === 'string')
    return (imageFields.has(field) ||
      imageMaps.has(parent) ||
      (parent === 'primary_image' && field === 'url')) &&
      /^\s*(?:data:image\/|blob:)/i.test(value)
      ? undefined
      : value;
  if (Array.isArray(value))
    return value
      .map((entry) => withoutBrowserImages(entry, '', field))
      .filter((entry) => entry !== undefined);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value).flatMap(([key, entry]) => {
      const clean = withoutBrowserImages(entry, key, field);
      return clean === undefined ? [] : [[key, clean]];
    }),
  );
}

export function serializeDemoState(state: AppState): string {
  return JSON.stringify(withoutBrowserImages(state));
}

/** Remove old photo payloads even when this visit uses a connected account. */
export function clearStoredDemoImages(store?: Storage): void {
  try {
    const target = store ?? localStorage;
    const keys = Array.from({ length: target.length }, (_, index) => target.key(index)).filter(
      (key): key is string => !!key && /^ggb\.local-demo\.v\d+$/.test(key),
    );
    for (const key of keys) {
      try {
        const raw = target.getItem(key);
        if (!raw) continue;
        const saved = JSON.parse(raw);
        const clean = JSON.stringify(withoutBrowserImages(saved));
        if (clean !== JSON.stringify(saved)) target.setItem(key, clean);
      } catch {}
    }
  } catch {
    // Shared images remain available when the browser denies localStorage access.
  }
}

export function createDemoState(): AppState {
  const people = [
    ['Alex Mercer', 'alexlifts', 'AM', '#59684d', 'Ironwood Garage', 'Raleigh, NC'],
    ['Miles Carter', 'milesbuilt', 'MC', '#825f45', 'Basement Barbell', 'Chicago, IL'],
    ['Jordan Ellis', 'jordansgarage', 'JE', '#57706c', 'The Sunday Studio', 'Denver, CO'],
    ['Sam Rivera', 'samliftsathome', 'SR', '#8d655d', 'Southwest Strength Shed', 'Austin, TX'],
    ['Devon Brooks', 'devonbuilds', 'DB', '#666984', 'Northside Lift Lab', 'Chicago, IL'],
  ];
  const establishedYears = [2021, 2019, 2023, 2020, 2022];
  const members: Member[] = people.map(
    ([display_name, username, initials, avatar_color, gym_name, location], i) => ({
      id: `demo-member-${i + 1}`,
      display_name,
      username,
      initials,
      avatar_color,
      avatar_url: demoMemberMedia(`demo-member-${i + 1}`)?.avatar,
      gym_name,
      established_year: establishedYears[i],
      location,
      is_private: false,
      bio: [
        'A black rack, a bit of plywood storage, and a 6 a.m. appointment with myself. Ironwood Garage is a sunlit one-car space in Raleigh built around simple strength work and room to move. Fictional sample member.',
        'Basement Barbell is my Chicago refuge: a red rack under exposed joists, heavy basics, and no wasted corners. I train after work and improve this little basement one weekend project at a time. Fictional sample member.',
        'Bright Denver mornings at The Sunday Studio. I turned a spare room into a place for dumbbells, rowing, and steady progress, with a few plants to keep it welcoming. Fictional sample member.',
        'Southwest Strength Shed is my backyard reset in Austin: plywood walls, a squat stand, and clear floor for a kettlebell circuit. Sharing the small upgrades that make training easier. Fictional sample member.',
        'Building Northside Lift Lab, a practical Chicago garage for strength and conditioning. Sage-green walls, a wooden lifting platform, and a fan bike make it feel like my own space. Always up for a layout conversation. Fictional sample member.',
      ][i],
      social_links: [],
    }),
  );
  const specs = [
    ['Foundation power rack', 'Racks', 'rack'],
    ['Adjustable utility bench', 'Benches', 'bench'],
    ['Everyday Olympic bar', 'Barbells', 'barbell'],
    ['Bumper plate set', 'Plates', 'plates'],
    ['Adjustable dumbbell pair', 'Dumbbells', 'dumbbells'],
    ['Compact cable trainer', 'Cable machines', 'cable'],
    ['Cast iron kettlebell', 'Kettlebells', 'kettlebell'],
    ['Vertical plate storage', 'Storage', 'storage'],
  ];
  const products: Product[] = specs.map(([name, category, image_kind], i): Product => ({
    id: `demo-product-${i + 1}`,
    manufacturer_id: 'demo-brand',
    brand: 'GGB Demo Equipment',
    name,
    category,
    slug: `example-${image_kind}`,
    description:
      'Fictional equipment used to demonstrate the catalog and inventory flow. This is not a real manufacturer product or offer. Specifications shown here are illustrative.',
    status: 'active',
    is_demo: true,
    variants: [
      {
        id: `demo-variant-${i + 1}`,
        product_id: `demo-product-${i + 1}`,
        name: i === 0 ? 'Standard height' : 'Standard',
        specifications: {},
      },
    ],
    specifications:
      i === 0
        ? {
            'Assembled height': '84 in (demo)',
            Footprint: '48 × 48 in (demo)',
            Loading: 'Plate loaded (demo)',
          }
        : {},
    image_kind,
    has_3d: false,
  }));
  const current_user_id = members[0].id;
  return {
    version: 1,
    current_user_id,
    members,
    products,
    owned_items: members.flatMap((member, memberIndex) =>
      Array.from({ length: 5 }, (_, i) => {
        const product = products[(i + memberIndex) % products.length];
        return {
          id: `demo-owned-${memberIndex + 1}-${i + 1}`,
          owner_id: member.id,
          gym_id: member.id === current_user_id ? 'demo-gym' : `demo-gym-${memberIndex + 1}`,
          product_id: product.id,
          variant_id: product.variants[0].id,
          custom_name: product.name,
          quantity: 1,
          condition: i === 0 ? 'Like new' : 'Good',
          ownership_status: 'owned' as const,
          visibility: 'public' as const,
          notes: [
            'The foundation of this garage. Kept clear for the main lifts.',
            'Easy to reach without blocking the walkway.',
            'A compact setup that earns its floor space.',
            'A favorite for short sessions between busy days.',
            'The finishing touch to this corner of the gym.',
          ][i],
          position: i,
          ...(member.id === current_user_id
            ? {
                purchase: {
                  amount: [450, 180, 150, 220, 280][i],
                  currency: 'USD',
                  source: 'Fictional demo purchase',
                  purchased_on: '2026-09-12',
                },
              }
            : {}),
        };
      }),
    ),
    reviews: [
      {
        id: 'demo-review-1',
        product_id: products[0].id,
        author_id: members[1].id,
        rating: 5,
        title: 'A great starting point',
        body: 'An example of a community review. This fictional rating stays entirely inside the local demo.',
        status: 'published',
        created_at: new Date().toISOString(),
        ownership_declaration: 'listed_in_gym',
        disclosure: 'Fictional demo review',
        helpful_ids: [],
      },
      {
        id: 'demo-review-2',
        product_id: products[0].id,
        author_id: members[2].id,
        rating: 4,
        title: 'Makes a small space work',
        body: 'Demo review showing how variant context and honest feedback can help other members.',
        status: 'published',
        created_at: new Date().toISOString(),
        ownership_declaration: 'used',
        disclosure: 'Fictional demo review',
        helpful_ids: [],
      },
    ],
    ...createCommunitySeed(members, current_user_id),
    reports: [],
    audit_events: [],
    catalog_view: 'grid',
    demo_role: 'member',
  };
}
export function readDemoState(store: Pick<Storage, 'getItem'> = localStorage): AppState {
  try {
    const saved = JSON.parse(store.getItem(DEMO_KEY) ?? 'null');
    if (
      saved?.version === 1 &&
      saved.members?.length === 5 &&
      saved.members.every((member: Member) => /^demo-member-[1-5]$/.test(member.id)) &&
      saved.members.some((member: Member) => member.id === saved.current_user_id)
    )
      return withoutBrowserImages(saved) as AppState;
  } catch {}
  return createDemoState();
}
